/**
 * 后台路由（D-015、D-020）：A01–A57 在 AdminLayout 内懒加载，每个页面路由带 `/*`（工作台 / 除外）；
 * 登录（/login，员工领域的 LoginPage）、激活（/activate）与重置（/reset）不在布局内；个人账户（/self）在布局内。
 */
import { useI18n } from '@nexora/i18n/react';
import { EmptyState } from '@nexora/ui';
import type { ComponentType } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { AdminLayout, type AdminRouteHandle } from './components/layout/AdminLayout';
import { ADMIN_AUX_PAGES, ADMIN_PAGES, adminAuxLoader } from './routes';

export function NotFound() {
  const { t } = useI18n();
  return <EmptyState title={t('error.NOT_FOUND')} />;
}

export const ROUTER_FUTURE = {
  v7_relativeSplatPath: true,
  v7_fetcherPersist: true,
  v7_normalizeFormMethod: true,
  v7_partialHydration: true,
  v7_skipActionErrorRevalidation: true,
} as const;

function lazyComponent(load: (() => Promise<{ default: ComponentType }>) | undefined) {
  return async () => ({ Component: load ? (await load()).default : NotFound });
}

export function createAdminRoutes(): RouteObject[] {
  const children: RouteObject[] = ADMIN_PAGES.map((page) => {
    const handle: AdminRouteHandle = { pageId: page.id };
    const lazy = lazyComponent(page.load);
    return page.path === '/' ? { index: true, handle, lazy } : { path: `${page.path.slice(1)}/*`, handle, lazy };
  });
  const outside: RouteObject[] = [{ path: '/login', lazy: async () => ({ Component: (await import('./LoginPage')).default }) }];
  for (const aux of ADMIN_AUX_PAGES) {
    const route: RouteObject = { lazy: lazyComponent(adminAuxLoader(aux)) };
    if (aux.inLayout) children.push({ ...route, path: aux.path.slice(1), handle: { titleKey: aux.titleKey } satisfies AdminRouteHandle });
    else outside.push({ ...route, path: aux.path });
  }
  children.push({ path: '*', element: <NotFound /> });

  return [...outside, { path: '/', element: <AdminLayout />, children }];
}

export function createAdminRouter() {
  return createBrowserRouter(createAdminRoutes(), { future: ROUTER_FUTURE });
}
